import * as z from 'zod'
import { getLangFallbackUrl } from '~/lang/utils/langfetch'
import { SearchFiltersUrl } from '../../url/search-filters/SearchFiltersUrl'
import { NearbyCampsites, SearchFilters } from './schemas'

export class NearbyRepositoryFetch {
  async get(
    slug: string,
    searchFilters: SearchFilters,
    langCode: string,
  ): Promise<NearbyCampsites> {
    const response = await $fetch<NearbyCampsitesResponse>(
      this.getURL(slug, searchFilters, langCode),
      {
        timeout: 15000,
      },
    )
    return nearbyCampsitesSchema.parse(response)
  }

  private getURL(slug: string, searchFilters: SearchFilters, langCode: string) {
    const queryParams = new URLSearchParams()
    const filtersToUrl = SearchFiltersUrl.filtersToUrl(searchFilters).query
    for (const [key, value] of Object.entries(filtersToUrl)) {
      if (value === undefined || !value?.length) continue
      if (Array.isArray(value)) {
        value.forEach((param) => {
          queryParams.append(key, param)
        })
      } else {
        queryParams.append(key, value)
      }
    }
    // for some reason filtersToUrl removes limit param so putting it here
    if (searchFilters.limit) {
      queryParams.append('limit', searchFilters.limit.toString())
    }
    return getLangFallbackUrl(
      langCode,
      `/_/endpoints/nearby-campsites/${slug}/?${queryParams.toString()}`,
    )
  }
}

const categorySchema = z.object({
  id: z.string(),
  rootSlug: z.string(),
  slug: z.string(),
  name: z.string(),
  order: z.number(),
  isByo: z.boolean(),
})

const distanceSchema = z.object({
  km: z.number(),
  mi: z.number(),
})

const moneySchemaResult = z.object({
  amount: z.number().optional(),
  amount_minor: z.number(),
  currency: z.string(),
})

const photoTransformsSchema = z.object({
  masterImage: z.string(),
})

const photoSchema = z.object({
  caption: z.string(),
  titleBy: z.string().optional(),
  url: photoTransformsSchema,
})

const nearbyCampsiteSchemaResponse = z.object({
  categories: categorySchema.array(),
  distance: distanceSchema.optional(),
  hierarchyPath: z.string(),
  hierarchyStringShort: z.string(),
  id: z.number().transform((v) => v.toString()),
  isBookable: z.boolean(),
  leadPrice: moneySchemaResult
    .optional()
    .nullable()
    .transform((v) => {
      return v
        ? {
            amount: v.amount,
            amountMinor: v.amount_minor,
            currency: v.currency,
          }
        : undefined
    }),
  leadPriceAdults: z
    .number()
    .optional()
    .nullable()
    .transform((v) =>
      v === 0 || v === null
        ? Number(useRuntimeConfig().public.defaultAdultsCount)
        : v,
    ),
  leadPriceNights: z
    .number()
    .optional()
    .nullable()
    .transform((v) => v || undefined),
  name: z.string(),
  point: z.string(),
  price: moneySchemaResult
    .optional()
    .nullable()
    .transform((v) => {
      return v
        ? {
            amount: v.amount,
            amountMinor: v.amount_minor,
            currency: v.currency,
          }
        : undefined
    }),
  primaryPhoto: photoSchema.nullable().optional(),
  rateCount: z.number(),
  rating: z.number().nullable(),
  slug: z.string(),
})

const nearbyCampsitesSchema = z.object({
  isDated: z.boolean(),
  results: nearbyCampsiteSchemaResponse.array(),
})

type NearbyCampsitesResponse = z.infer<typeof nearbyCampsitesSchema>
